import { Injectable, Injector, inject } from '@angular/core';
import {
  CoreBackendIDs,
  CoreHttpClient,
  CoreMobiResource,
  CoreMobiResourceType,
  CORE_BACKEND
} from '@bepatient/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Category } from './category.models';

/**
 * Service responsable des calls API pour les catégories
 */
@Injectable()
export class CategoryApiService {
  private injector = inject(Injector);
  private http = inject(CoreHttpClient);

  /**
   * Charge une catégorie
   *
   * @param href Le href de la catégorie à charger
   */
  loadCategory(href: string): Observable<Category> {
    switch (this.injector.get(CORE_BACKEND)) {
      case CoreBackendIDs.SMART:
        return this.http
          .get(href, { withMobiCredentials: true })
          .pipe(map(res => this.categoryNewParser(res)));
      default:
        return this.http
          .get(href, { withMobiCredentials: true })
          .pipe(map(res => this.categoryParser(res)));
    }
  }

  /**
   * Parse une catégorie envoyée par MOBI
   *
   * @param data Les données retournées par MOBI
   */
  private categoryParser(data: any): Category {
    const convertResource = (type: string, href: string): CoreMobiResource => {
      switch (type) {
        case 'form':
          return { type: CoreMobiResourceType.FORM, href };
        case 'category':
          return { type: CoreMobiResourceType.CATEGORY, href };
        case 'forms-container':
          return { type: CoreMobiResourceType.CATEGORY, href };
        case 'fiche':
        case 'factsheet':
          return { type: CoreMobiResourceType.FACTSHEET, href };
        case 'quizz':
          return { type: CoreMobiResourceType.QUIZZ, href };
        case 'body-schema-pain':
          return { type: CoreMobiResourceType.BODY_SCHEMA, href };
        default:
          return void 0;
      }
    };

    return {
      color: (data.meta && data.meta.color) || '#faba8c',
      title: (data.meta && data.meta.title) || '',
      image: (data.meta && data.meta.thumbnail) || '',
      items: (data.items || []).map(item => ({
        color: (item.meta && item.meta.color) || '#fff',
        title: (item.meta && item.meta.title) || '',
        image: (item.meta && item.meta.thumbnail) || '',
        progress: (item.meta && item.meta.last_activity) || 0,
        resource: convertResource(
          (item.meta && item.meta.type) || '',
          (item.links && item.links.self && item.links.self.href) || ''
        ),
        htmlContent:
          (item.meta && item.meta.type === 'html' && item.meta.html) || void 0
      }))
    };
  }

  /**
   * New category parser for SMART
   *
   * @param data Returned by SMART
   */
  private categoryNewParser(data: any): Category {
    const convertResource = (type: string, href: string): CoreMobiResource => {
      switch (type) {
        case 'form':
          return { type: CoreMobiResourceType.FORM, href };
        case 'category':
          return { type: CoreMobiResourceType.CATEGORY, href };
        case 'forms-container':
          return { type: CoreMobiResourceType.CATEGORY, href };
        case 'fiche':
        case 'factsheet':
          return { type: CoreMobiResourceType.FACTSHEET, href };
        case 'quizz':
          return { type: CoreMobiResourceType.QUIZZ, href };
        case 'body-schema-pain':
          return { type: CoreMobiResourceType.BODY_SCHEMA, href };
        case 'html':
          return { type: CoreMobiResourceType.HTML, href: void 0 };
        default:
          return void 0;
      }
    };

    return {
      color: data.payload.color || '',
      title: data.payload.title || '',
      image: data.payload.thumbnail || '',
      icon: data.payload.icon || '',
      items: (data.payload.items || []).map(item => ({
        color: item.color || '',
        title: item.title || '',
        image: item.thumbnail || '',
        progress: item.progress || 0,
        icon: item.icon || '',
        resource: convertResource(item.type || '', item.href || ''),
        htmlContent: (item.type === 'html' && item.html) || void 0
      }))
    };
  }

  /**
   * @ignore
   */
  private mockedCategoryNew(): any {
    return {
      color: '#faba8c',
      title: 'ePRO - Validated Questionnaires',
      thumbnail:
        'https://dev-mobi-julien.bepatientsolutions.com/images/dummy_image_category',
      items: [
        {
          color: '#fc907b',
          title: 'SF-12 Health Survey',
          thumbnail:
            'https://dev-demo-bp-cardio.bepatientsolutions.com/phoenix/eventa/core/images/base/pages/page_wait.png',
          status: 'done',
          type: 'form',
          href: 'https://local-smart-webservice.bepatientsolutions.com/rest/contents/forms/1064'
        },
        {
          color: '#647aec',
          title: 'Category Files',
          thumbnail:
            'https://dev-mobi-julien.bepatientsolutions.com/images/dummy_image_category',
          status: 'done',
          type: 'category',
          href: 'https://local-smart-webservice.bepatientsolutions.com/rest/contents/categories/50'
        },
        {
          color: '#647aec',
          title: 'Simple Html',
          thumbnail:
            'https://dev-demo-bp-cardio.bepatientsolutions.com/phoenix/eventa/core/images/base/pages/page_blank.png',
          status: 'done',
          type: 'html',
          htmlContent: '<p>This is a test description for type html 1</p>'
        }
      ]
    };
  }

  /**
   * @ignore
   */
  private mockedCategory(): Category {
    return {
      color: '#faba8c',
      title: 'Les médicaments',
      image: 'https://dev-mobi-julien.bepatientsolutions.com/images/30',
      items: [
        {
          color: '#fc907b',
          title: 'Alimentation équilibrée en sel',
          image: 'https://dev-mobi-julien.bepatientsolutions.com/images/30',
          progress: 0,
          resource: void 0
        },
        {
          color: '#fc907b',
          title: 'Votre coeur, cette pompe naturelle',
          image: 'https://dev-mobi-julien.bepatientsolutions.com/images/30',
          progress: 0,
          resource: void 0
        },
        {
          color: '#fc907b',
          title: 'Organiser votre emploi du temps',
          image: 'https://dev-mobi-julien.bepatientsolutions.com/images/30',
          progress: 0,
          resource: void 0
        }
      ]
    };
  }
}
